import React, { useEffect, useState } from "react";
import { Col, Row, Card } from "react-bootstrap";
import Footer from "../../Components/common/Footer";
import Header from "../../Components/common/Header";
import Layout from "../../Components/Website/Layout";
import "../../Pages/Website/Courses.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ACADIMAC, baseURL } from "../../API/API";
import useAuth from "../hooks/useAuth";
import { toast } from "react-toastify";
import { FaRegCalendarAlt, FaRegClock } from "react-icons/fa";
import { resolveMediaUrl } from "../../utils/mediaUrl";

// Import images
import Img1 from "../../Imge/7.png";
import Img2 from "../../Imge/8.png";
import Img3 from "../../Imge/3.png";
import Img4 from "../../Imge/4.png";
import Img5 from "../../Imge/5.png";
import Img6 from "../../Imge/6.png";
import FallbackImg from "../../Imge/about_2.png";

const fallbackStages = [
  { id: 1, name: "الصف الأول الثانوي", academic_type: "general" },
  { id: 2, name: "الصف الثاني الثانوي", academic_type: "general" },
  { id: 3, name: "الصف الثالث الثانوي", academic_type: "general" },
  { id: 4, name: "الصف الأول الثانوي أزهري", academic_type: "azhar" },
  { id: 5, name: "الصف الثاني الثانوي أزهري", academic_type: "azhar" },
  { id: 6, name: "الصف الثالث الثانوي أزهري", academic_type: "azhar" }
];

// Helper: detect azhar
const isAzharStage = (stage) => {
  const name = stage.name?.toLowerCase() || "";
  const type = stage.academic_type?.toLowerCase() || "";
  return (
    type === "azhar" ||
    name.includes("أزهر") ||
    name.includes("ازهر")
  );
};

// Helper: detect grade order (1=أول, 2=ثاني, 3=ثالث)
const getGradeOrder = (name) => {
  const n = name?.toLowerCase() || "";
  if (n.includes("أول") || n.includes("اول") || n.includes("1")) return 1;
  if (n.includes("ثان") || n.includes("تان") || n.includes("2")) return 2;
  if (n.includes("ثالث") || n.includes("تالت") || n.includes("3")) return 3;
  return 4;
};

// Sort: مرتبة حسب ترتيب العرض المدخل في الداشبورد أولاً، ثم حسب القسم والصف كاحتياطي
const sortStages = (stages) => {
  return [...stages].sort((a, b) => {
    const orderA = a.sort_order ?? a.sortOrder ?? a.order ?? 0;
    const orderB = b.sort_order ?? b.sortOrder ?? b.order ?? 0;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    const aIsAzhar = isAzharStage(a) ? 1 : 0;
    const bIsAzhar = isAzharStage(b) ? 1 : 0;
    if (aIsAzhar !== bIsAzhar) return aIsAzhar - bIsAzhar;
    return getGradeOrder(a.name) - getGradeOrder(b.name);
  });
};

const renderStageImage = (stage) => {
  if (stage?.image_path) {
    return <img src={resolveMediaUrl(stage.image_path)} alt={stage.name} className="w-100 h-100 object-fit-cover stage-card-img" />;
  }
  
  const isAzhar = isAzharStage(stage);
  const bgGradient = isAzhar
    ? "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)"
    : "linear-gradient(135deg, #4c0d16 0%, #a21caf 100%)";
    
  return (
    <div 
      className="w-100 h-100 d-flex flex-column align-items-center justify-content-center position-relative"
      style={{ 
        background: bgGradient, 
        padding: "20px",
        color: "#fff",
        overflow: "hidden"
      }}
    >
      <div style={{
        position: "absolute",
        width: "150px",
        height: "150px",
        borderRadius: "50%",
        background: "rgba(255,255,255,0.05)",
        top: "-50px",
        right: "-50px"
      }} />
      <div style={{
        position: "absolute",
        width: "100px",
        height: "100px",
        borderRadius: "50%",
        background: "rgba(255,255,255,0.03)",
        bottom: "-30px",
        left: "-30px"
      }} />
      
      <img 
        src="/LogoNohe.png" 
        alt="شعار المنصة" 
        style={{ 
          width: "80px", 
          height: "80px", 
          objectFit: "contain",
          marginBottom: "15px",
          filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.15))"
        }} 
      />
      
      <div className="text-center fw-bold" style={{ zIndex: 1, fontSize: "1.2rem", letterSpacing: "0.5px" }}>
        {stage.name || stage.academicName}
      </div>
      <div 
        className="text-white-50 mt-2" 
        style={{ 
          fontSize: "0.82rem", 
          backgroundColor: "rgba(255,255,255,0.15)",
          padding: "2px 12px",
          borderRadius: "12px",
          fontWeight: "600"
        }}
      >
        {isAzhar ? "القسم الأزهري" : "القسم العام"}
      </div>
    </div>
  );
};

export default function Courses() {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { auth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${baseURL}/${ACADIMAC}`)
      .then((response) => {
        const data = response.data?.data || [];
        if (data.length > 0) {
          setStages(sortStages(data));
        } else {
          setStages(sortStages(fallbackStages));
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching academic years for Courses page:", error);
        setStages(sortStages(fallbackStages));
        setLoading(false);
      });
  }, []);

  const handleCourseClick = (stage) => {
    if (auth?.accessToken && auth.user?.is_staff) {
      navigate(`/learn/${stage.id}`);
      return;
    }

    const id = Number(stage.id);
    const isAzhar = isAzharStage(stage);
    let gradeNum = 1;
    if (id === 13 || id === 14 || id === 2 || id === 5) {
      gradeNum = 2;
    } else if (id === 12 || id === 11 || id === 3 || id === 6) {
      gradeNum = 3;
    }
    navigate(`/courses/grade/${gradeNum}?system=${isAzhar ? "azhar" : "general"}`);
  };

  const handleSubscribeClick = (stage) => {
    const id = Number(stage.id);
    const isAzhar = isAzharStage(stage);
    let gradeNum = 1;
    if (id === 13 || id === 14 || id === 2 || id === 5) {
      gradeNum = 2;
    } else if (id === 12 || id === 11 || id === 3 || id === 6) {
      gradeNum = 3;
    }
    navigate(`/courses/grade/${gradeNum}?system=${isAzhar ? "azhar" : "general"}`);
  };

  const getStageImage = (stage) => {
    if (stage?.image_path) return resolveMediaUrl(stage.image_path);
    
    const id = Number(stage?.id);
    if (id === 16 || id === 1) return Img1;
    if (id === 13 || id === 2) return Img2;
    if (id === 12 || id === 3) return Img3;
    if (id === 10 || id === 4) return Img4;
    if (id === 14 || id === 5) return Img5;
    if (id === 11 || id === 6) return Img6;
    
    return FallbackImg;
  };

  const getStageDetails = (stage) => {
    const title = stage?.name || stage?.academicName || "";
    const price = stage?.price !== undefined && stage?.price !== null ? `${stage.price} جنيه` : "150 جنيه";
    const desc = stage?.description || "";
    const azhar = isAzharStage(stage);
    const subtitle = stage?.subtitle || (azhar ? "ثانوية أزهرية" : "ثانوي عام");

    return {
      title: title,
      price: price,
      desc: desc,
      subtitle: subtitle,
    };
  };

  return (
    <div className="CoursesPage" dir="rtl">
      <Header />
      <div className="courses-header-section text-center py-5">
        <Layout>
          <h1 className="courses-main-title fw-bold">اختر المرحلة التعليمية</h1>
          <p className="courses-subtitle text-muted">اختر مرحلتك الدراسية لتظهر لك الدروس المتاحة والاشتراك في الكورسات</p>
        </Layout>
      </div>

      <div className="courses-grid-section py-5 bg-light">
        <Layout>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">جاري التحميل...</span>
              </div>
              <p className="mt-3 text-muted">جاري تحميل المراحل الدراسية...</p>
            </div>
          ) : stages.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <p>لا توجد مراحل دراسية متاحة حالياً.</p>
            </div>
          ) : (
            <Row className="g-4 justify-content-center">
              {stages.map((stage) => {
                const img = getStageImage(stage);
                const details = getStageDetails(stage);
                const isMyCourse = auth?.accessToken && (auth.user?.is_staff || Number(stage.id) === Number(auth.user?.academic_year_id));

                return (
                  <Col key={stage.id} xs={12} md={6} lg={4} className="d-flex">
                    <Card className={`stage-card w-100 suggested-course-card ${isMyCourse ? "my-stage" : ""}`}>
                      <div className="position-relative overflow-hidden stage-image-wrapper" style={{ height: "240px" }}>
                        {renderStageImage(stage)}
                      </div>
                      <Card.Body className="d-flex flex-column text-end p-4">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <div className="text-lg fw-bold course-card-title m-0">{details.title}</div>
                          <span className="stage-card-subtitle-custom" style={{
                            fontFamily: "AminFont, sans-serif",
                            fontSize: "0.85rem",
                            fontWeight: "500",
                            color: "var(--brand-200-alt, #cca245)",
                            whiteSpace: "nowrap"
                          }}>{details.subtitle}</span>
                        </div>
                        <div className="fw-bold mb-2 course-card-price" style={{ color: "var(--brand-300)" }}>{details.price}</div>
                        <div className="course-meta-info d-flex justify-content-between align-items-center mb-3 small text-muted">
                          <span><FaRegCalendarAlt /> الاثنين، 6 أبريل</span>
                          <span><FaRegClock /> 2:00 م</span>
                        </div>
                        <div className="small text-muted flex-grow-1 mb-4" style={{ lineHeight: "1.7" }}>
                          {details.desc}
                        </div>
                        <div className="d-flex flex-column gap-2 mt-auto pt-2">
                          <button 
                            onClick={() => handleCourseClick(stage)} 
                            className="btn btn-enter-course w-100 rounded-xl py-2"
                            style={{
                              border: "1.5px solid var(--brand-300)",
                              color: "var(--brand-300)",
                              backgroundColor: "transparent",
                              fontWeight: "bold"
                            }}
                          >
                            الدخول للكورس
                          </button>
                          <button 
                            onClick={() => handleSubscribeClick(stage)} 
                            className="btn btn-subscribe-course w-100 rounded-xl py-2"
                            style={{
                              backgroundColor: "var(--brand-300)",
                              border: "none",
                              color: "#ffffff",
                              fontWeight: "bold"
                            }}
                          >
                            الإشتراك في الكورس !
                          </button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Layout>
      </div>
      <Footer />
    </div>
  );
}
